import React from 'react';
import Document from '@uber/base-icons/document';
import { useStyletron } from 'baseui';
import type { ListOverrides } from 'baseui/list';
import type { TagOverrides } from 'baseui/tag';
import type { AnnotationLabelT } from '../../../common/types/types';
import { TASK_QUESTIONNAIRE_ID, TASK_QUESTIONNAIRE_TITLE } from '../../side-panel/const';
import { LabelItem } from '../../side-panel/labels/label-item';
import { RequiredLabelBadge } from '../../side-panel/labels/required-label-badge';
type TaskQuestionnaireComponentT = {
  Component: ({
    onClose
  }: {
    onClose?: () => void;
  }) => React.ReactNode;
  status: 'PENDING' | 'DONE' | 'OPTIONAL';
};
type PropsT = {
  labels: AnnotationLabelT[];
  annotationLabels: AnnotationLabelT[];
  selectedLabel?: AnnotationLabelT;
  annotationCountMap: Record<string, number>;
  onLabelClick: (name: string) => void;
  onCountClick: (name: string) => void;
  handleLabelColorChange: (payload: {
    color: string;
    title: string;
  }) => boolean;
  labelListItemEnhancer?: (labelTitle: string) => React.ReactNode;
  hideCountTagForLabel?: (labelTitle: string) => boolean;
  getRenderIcon: (label: AnnotationLabelT) => (props: {
    strokeColor: string;
  }) => React.ReactNode;
  taskQuestionnaireComponent?: TaskQuestionnaireComponentT;
  onOpenTaskQuestionnaire?: () => void;
  shouldShowTaskQuestionnaire?: boolean;
};
const getTagOverrides = (isSelected: boolean): TagOverrides => {
  return {
    Root: {
      style: ({
        $theme
      }) => ({
        margin: 0,
        display: 'flex',
        flexDirection: 'row-reverse',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'none',
        padding: `${$theme.sizing.scale0} ${$theme.sizing.scale100}`,
        height: $theme.sizing.scale700,
        backgroundColor: isSelected ? $theme.colors.blue100Dark : $theme.colors.blue100,
        color: isSelected ? $theme.colors.blue700Dark : $theme.colors.blue700
      })
    },
    Text: {
      style: ({
        $theme
      }) => ({
        ...$theme.typography.LabelXSmall
      })
    }
  };
};
const getLabelListItemOverrides = (isSelected: boolean): ListOverrides => ({
  Root: {
    style: ({
      $theme
    }) => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderBottom: `1px solid ${isSelected ? 'transparent' : $theme.colors.borderOpaque}`,
      padding: $theme.sizing.scale300,
      width: '100%',
      backgroundColor: isSelected ? $theme.colors.backgroundTertiary : 'transparent',
      borderRadius: isSelected ? $theme.borders.radius300 : 'none',
      transitionProperty: 'background-color, border',
      transitionDuration: '0.2s',
      transitionTimingFunction: 'ease-in',
      ':active': {
        backgroundColor: $theme.colors.backgroundSecondary,
        borderRadius: $theme.borders.radius300
      }
    })
  },
  ArtworkContainer: {
    style: ({
      $theme
    }) => ({
      minHeight: 0,
      width: 'auto',
      paddingRight: $theme.sizing.scale500
    })
  },
  Content: {
    style: ({
      $theme
    }) => ({
      minHeight: 0,
      padding: 0,
      gap: $theme.sizing.scale200
    })
  },
  EndEnhancerContainer: {
    style: {
      padding: 0
    }
  }
});
export const LabelList = ({
  labels,
  annotationLabels,
  selectedLabel,
  annotationCountMap,
  onLabelClick,
  onCountClick,
  handleLabelColorChange,
  labelListItemEnhancer,
  hideCountTagForLabel,
  getRenderIcon,
  taskQuestionnaireComponent,
  onOpenTaskQuestionnaire,
  shouldShowTaskQuestionnaire = false
}: PropsT) => {
  const [css] = useStyletron();
  const taskQuestionnaireShortcutKey = annotationLabels.length < 9 ? annotationLabels.length + 1 : undefined;
  return <div className={css({
    overflowY: 'auto'
  })}>
      {labels.map(label => {
      const isSelected = label.title === selectedLabel?.title;
      const originalIndex = annotationLabels.findIndex(l => l.title === label.title);
      return <LabelItem key={label.title} shortcutKey={originalIndex < 9 ? originalIndex + 1 : undefined} isM4Enabled={true} hideCountTag={hideCountTagForLabel?.(label.title)} id={label.title} name={label.title} endEnhancer={labelListItemEnhancer?.(label.title)} isSelected={isSelected} annotationCount={annotationCountMap[label.title]} onClick={onLabelClick} onCountClick={onCountClick} handleLabelColorChange={handleLabelColorChange} color={label.style.strokeColor} renderIcon={getRenderIcon(label)} getListItemOverrides={getLabelListItemOverrides} getTagOverrides={getTagOverrides} />;
    })}
      {shouldShowTaskQuestionnaire && taskQuestionnaireComponent && <LabelItem key={TASK_QUESTIONNAIRE_ID} shortcutKey={taskQuestionnaireShortcutKey} isM4Enabled={true} hideCountTag={true} id={TASK_QUESTIONNAIRE_ID} name={TASK_QUESTIONNAIRE_TITLE} endEnhancer={taskQuestionnaireComponent.status !== 'OPTIONAL' ? <RequiredLabelBadge /> : null} isSelected={false} onClick={() => onOpenTaskQuestionnaire?.()} onCountClick={() => {}} handleLabelColorChange={() => false} color="transparent" renderIcon={() => <Document />} getListItemOverrides={getLabelListItemOverrides} getTagOverrides={getTagOverrides} />}
    </div>;
};
export default LabelList;